import { useMemo } from 'react';
import cm from 'classnames';

import { FileStatus, FileType } from 'services/file.service';
import { useAppSelector } from 'store/store-hooks';
import { IntroKeys } from 'components/app/intro/intro-keys';
import { Button } from 'components/UI/button/button';
import { PickerBase } from '../files-picker/picker-base';
import styles from './_placeholders.module.scss';

interface Payload {
  audio: boolean;
  media: boolean;
}

export const PlaceholderFiles = () => {
  const projectId = useAppSelector((state) => state.projects.selectedProject);
  const uploadingFiles = useAppSelector((state) => state.app__filesUpload.uploadingFiles);
  const selectedProject = useAppSelector((state) => state.projects.selectedProject);
  const editorAvailable = useAppSelector((state) => state.app.editorAvailable);
  const fileStatuses = useAppSelector((state) => state.app__files.statuses);
  const files = useAppSelector((state) => state.app__files.files);

  const payload: Payload | undefined = useMemo(() => {
    if (projectId === null || editorAvailable) return;
    const filesArr = Object.values(files);
    const isAudioUploading = uploadingFiles.some((file) => file.projectId === projectId && isAudio(file.type));
    const isMediaUploading = uploadingFiles.some((file) => file.projectId === projectId && isMedia(file.type));
    const isAudioPresent = filesArr.some((file) => isAudio(file.fileType) && file.status !== FileStatus.Error);
    const isMediaPresent = filesArr.some((file) => isMedia(file.fileType) && file.status !== FileStatus.Error);
    return {
      audio: isAudioUploading || isAudioPresent,
      media: isMediaUploading || isMediaPresent,
    };
  }, [editorAvailable, uploadingFiles.length, files, fileStatuses]);

  const getTitle = (payload: Payload) => {
    if (!payload.audio && !payload.media) return 'Add a music file and at least one video or image file';
    if (!payload.media) return 'Add at least one video or image file';
    if (!payload.audio) return 'Add a music file';
  };

  if (!selectedProject || !payload) return null;
  if (payload.audio && payload.media) return null;

  return (
    <div className={styles.Placeholder}>
      <div className={styles.Placeholder__container}>
        <div className={styles.Placeholder__title}>{getTitle(payload)}</div>
        <PickerBase>
          <Button className={cm(styles.Placeholder__button)} data-intro={IntroKeys.addFiles}>
            Add Files
          </Button>
        </PickerBase>
      </div>
    </div>
  );
};

/* H E L P E R S */

const isAudio = (fileType: FileType) => {
  return fileType === FileType.audio;
};

const isMedia = (fileType: FileType) => {
  return [FileType.image, FileType.video].includes(fileType);
};
